// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/** Pure analytics over the incident list. No DOM — unit-tested in tests/. */
import type { Incident, Outcome } from './types';
import {
  OUTCOME_ORDER,
  ACTIVITY_ORDER,
  STATE_ORDER,
  STATE_POP,
  SPECIES,
  speciesInfo,
} from './domain';

export type OutcomeCounts = Record<Outcome, number>;

export function emptyOutcomes(): OutcomeCounts {
  return { fatal: 0, injured: 0, uninjured: 0, unknown: 0 };
}

export function outcomeCounts(incidents: Incident[]): OutcomeCounts {
  const c = emptyOutcomes();
  for (const it of incidents) c[it.out]++;
  return c;
}

/** Fatal share of incidents whose outcome is known (fatal/injured/uninjured). */
export function fatalPct(c: OutcomeCounts): number {
  const known = c.fatal + c.injured + c.uninjured;
  return known ? (c.fatal / known) * 100 : 0;
}

export function countBy<T>(items: T[], keyFn: (t: T) => string | number | null): Map<string, number> {
  const m = new Map<string, number>();
  for (const it of items) {
    const k = keyFn(it);
    if (k == null) continue;
    const key = String(k);
    m.set(key, (m.get(key) || 0) + 1);
  }
  return m;
}

export function median(nums: number[]): number | null {
  if (!nums.length) return null;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

// ── Time ──────────────────────────────────────────────────────────────────────
export interface DecadeRow {
  decade: number;
  label: string;
  total: number;
  fatal: number;
  injured: number;
  uninjured: number;
  unknown: number;
  fatalPct: number;
  partial: boolean;
}

export function decadeSeries(incidents: Incident[], dataThrough: number): DecadeRow[] {
  const map = new Map<number, OutcomeCounts>();
  for (const it of incidents) {
    if (it.y == null) continue;
    const dec = Math.floor(it.y / 10) * 10;
    if (!map.has(dec)) map.set(dec, emptyOutcomes());
    map.get(dec)![it.out]++;
  }
  return [...map.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([decade, c]) => ({
      decade,
      label: `${decade}s`,
      total: c.fatal + c.injured + c.uninjured + c.unknown,
      fatal: c.fatal,
      injured: c.injured,
      uninjured: c.uninjured,
      unknown: c.unknown,
      fatalPct: fatalPct(c),
      partial: decade + 9 > dataThrough,
    }));
}

export interface YearRow {
  year: number;
  total: number;
  fatal: number;
}
export function yearSeries(incidents: Incident[]): YearRow[] {
  const map = new Map<number, { total: number; fatal: number }>();
  for (const it of incidents) {
    if (it.y == null) continue;
    if (!map.has(it.y)) map.set(it.y, { total: 0, fatal: 0 });
    const r = map.get(it.y)!;
    r.total++;
    if (it.out === 'fatal') r.fatal++;
  }
  return [...map.entries()].sort((a, b) => a[0] - b[0]).map(([year, r]) => ({ year, ...r }));
}

// ── Species ─────────────────────────────────────────────────────────────────
export interface SpeciesRow {
  key: string;
  label: string;
  total: number;
  fatal: number;
  injured: number;
  uninjured: number;
  fatalPct: number;
  big3: boolean;
}
export function speciesStats(incidents: Incident[]): SpeciesRow[] {
  const map = new Map<string, OutcomeCounts>();
  for (const it of incidents) {
    if (!map.has(it.sp)) map.set(it.sp, emptyOutcomes());
    map.get(it.sp)![it.out]++;
  }
  return [...map.entries()]
    .map(([key, c]) => ({
      key,
      label: speciesInfo(key).label,
      total: c.fatal + c.injured + c.uninjured + c.unknown,
      fatal: c.fatal,
      injured: c.injured,
      uninjured: c.uninjured,
      fatalPct: fatalPct(c),
      big3: !!SPECIES[key]?.big3,
    }))
    .sort((a, b) => b.total - a.total);
}

// ── Activities ────────────────────────────────────────────────────────────────
export interface ActivityRow {
  key: string;
  total: number;
  fatal: number;
  provoked: number;
  unprovoked: number;
  fatalPct: number;
  provokedPct: number;
}
export function activityStats(incidents: Incident[]): ActivityRow[] {
  const map = new Map<string, { c: OutcomeCounts; prov: number; unprov: number }>();
  for (const it of incidents) {
    if (!map.has(it.act)) map.set(it.act, { c: emptyOutcomes(), prov: 0, unprov: 0 });
    const r = map.get(it.act)!;
    r.c[it.out]++;
    if (it.prov === true) r.prov++;
    else if (it.prov === false) r.unprov++;
  }
  return ACTIVITY_ORDER.filter((k) => map.has(k)).map((key) => {
    const r = map.get(key)!;
    const total = r.c.fatal + r.c.injured + r.c.uninjured + r.c.unknown;
    return {
      key,
      total,
      fatal: r.c.fatal,
      provoked: r.prov,
      unprovoked: r.unprov,
      fatalPct: fatalPct(r.c),
      provokedPct: r.prov + r.unprov ? (r.prov / (r.prov + r.unprov)) * 100 : 0,
    };
  });
}

// ── States ────────────────────────────────────────────────────────────────────
export interface StateRow {
  st: string;
  total: number;
  fatal: number;
  fatalPct: number;
  perMillion: number | null;
}
export function stateStats(incidents: Incident[]): StateRow[] {
  const map = new Map<string, OutcomeCounts>();
  for (const it of incidents) {
    if (!it.st) continue;
    if (!map.has(it.st)) map.set(it.st, emptyOutcomes());
    map.get(it.st)![it.out]++;
  }
  return STATE_ORDER.filter((s) => map.has(s)).map((st) => {
    const c = map.get(st)!;
    const total = c.fatal + c.injured + c.uninjured + c.unknown;
    const pop = STATE_POP[st];
    return {
      st,
      total,
      fatal: c.fatal,
      fatalPct: fatalPct(c),
      perMillion: pop ? (total / pop) * 1e6 : null,
    };
  }).sort((a, b) => b.total - a.total);
}

// ── Seasonality ───────────────────────────────────────────────────────────────
export function monthCounts(incidents: Incident[]): number[] {
  const arr = new Array(12).fill(0);
  for (const it of incidents) if (it.m) arr[it.m - 1]++;
  return arr;
}

/** month (rows 0-11) × outcome matrix of counts. */
export function monthOutcomeMatrix(incidents: Incident[]): number[][] {
  const rows = Array.from({ length: 12 }, () => OUTCOME_ORDER.map(() => 0));
  for (const it of incidents) {
    if (!it.m) continue;
    const oi = OUTCOME_ORDER.indexOf(it.out);
    rows[it.m - 1][oi]++;
  }
  return rows;
}

export function periodOfDayCounts(incidents: Incident[]): Record<string, number> {
  const c: Record<string, number> = { dawn: 0, day: 0, dusk: 0, night: 0 };
  for (const it of incidents) if (it.pod) c[it.pod]++;
  return c;
}

// ── Cross-reference: species (rows) × activity (cols) ─────────────────────────
export function speciesActivityMatrix(
  incidents: Incident[],
  speciesKeys: string[],
  activityKeys: string[],
): number[][] {
  const rIdx = new Map(speciesKeys.map((k, i) => [k, i]));
  const cIdx = new Map(activityKeys.map((k, i) => [k, i]));
  const m = speciesKeys.map(() => activityKeys.map(() => 0));
  for (const it of incidents) {
    const r = rIdx.get(it.sp);
    const c = cIdx.get(it.act);
    if (r == null || c == null) continue;
    m[r][c]++;
  }
  return m;
}

// ── Age histogram ─────────────────────────────────────────────────────────────
export interface HistBin {
  lo: number;
  hi: number;
  label: string;
  count: number;
}
export function ageBins(incidents: Incident[], width = 10, max = 80): HistBin[] {
  const bins: HistBin[] = [];
  for (let lo = 0; lo < max; lo += width) bins.push({ lo, hi: lo + width, label: `${lo}–${lo + width - 1}`, count: 0 });
  bins.push({ lo: max, hi: Infinity, label: `${max}+`, count: 0 });
  for (const it of incidents) {
    if (it.age == null) continue;
    const idx = Math.min(bins.length - 1, Math.floor(it.age / width));
    bins[idx].count++;
  }
  return bins;
}

/** Year histogram binned by decade (for the explorer click-filter). */
export function decadeBins(incidents: Incident[]): HistBin[] {
  const years = incidents.map((i) => i.y).filter((y): y is number => y != null);
  if (!years.length) return [];
  const lo = Math.floor(Math.min(...years) / 10) * 10;
  const hi = Math.floor(Math.max(...years) / 10) * 10;
  const bins: HistBin[] = [];
  for (let d = lo; d <= hi; d += 10) bins.push({ lo: d, hi: d + 10, label: `${d}s`, count: 0 });
  for (const y of years) {
    const idx = Math.floor((y - lo) / 10);
    if (bins[idx]) bins[idx].count++;
  }
  return bins;
}

// ── Insights (auto-detected) ─────────────────────────────────────────────────
export interface Insight {
  severity: 'info' | 'warn' | 'alert' | 'good';
  title: string;
  body: string;
  view?: string;
}

export function buildInsights(incidents: Incident[], dataThrough: number): Insight[] {
  const out: Insight[] = [];
  const decs = decadeSeries(incidents, dataThrough).filter((d) => !d.partial);
  const species = speciesStats(incidents);
  const acts = activityStats(incidents);
  const states = stateStats(incidents);
  const months = monthCounts(incidents);
  const all = outcomeCounts(incidents);

  // Falling fatality rate.
  const early = decs.filter((d) => d.decade < 1940);
  const recent = decs.filter((d) => d.decade >= 2000);
  const earlyRate = early.reduce((s, d) => s + d.fatal, 0) / Math.max(1, early.reduce((s, d) => s + d.fatal + d.injured + d.uninjured, 0)) * 100;
  const recentRate = recent.reduce((s, d) => s + d.fatal, 0) / Math.max(1, recent.reduce((s, d) => s + d.fatal + d.injured + d.uninjured, 0)) * 100;
  if (recent.length) {
    out.push({
      severity: 'good',
      title: 'Bites are up, but far more survivable',
      body: `The share of incidents that prove fatal has fallen from about ${earlyRate.toFixed(0)}% before 1940 to roughly ${recentRate.toFixed(0)}% since 2000 — even as the number of recorded incidents rose to record highs. Faster rescue and better trauma care, not calmer sharks.`,
      view: 'time',
    });
  }

  // Three species dominate fatalities.
  const totalFatal = all.fatal;
  const big3Fatal = species.filter((s) => s.big3).reduce((s, r) => s + r.fatal, 0);
  if (totalFatal > 0) {
    out.push({
      severity: 'alert',
      title: 'Three species cause almost every death',
      body: `White, tiger and bull sharks account for ${big3Fatal} of ${totalFatal} fatal bites (${((big3Fatal / totalFatal) * 100).toFixed(0)}%). Most other species — wobbegongs, whalers, grey nurse — leave only minor wounds.`,
      view: 'species',
    });
  }

  // Wobbegong: many bites, zero deaths.
  const wob = species.find((s) => s.key === 'wobbegong');
  if (wob && wob.total > 20 && wob.fatal === 0) {
    out.push({
      severity: 'info',
      title: `${wob.total} wobbegong bites, none fatal`,
      body: `The wobbegong is behind ${wob.total} recorded incidents yet no deaths — nearly all are defensive nips after a diver or wader stood on or grabbed one. A reminder that "bite" rarely means "attack".`,
      view: 'species',
    });
  }

  // Swimming most common and most lethal.
  const swim = acts.find((a) => a.key === 'swimming');
  const board = acts.find((a) => a.key === 'board-riding');
  if (swim && board) {
    out.push({
      severity: 'warn',
      title: 'Swimmers face the worst odds',
      body: `Swimming accounts for the most incidents (${swim.total}) and the highest fatality rate (${swim.fatalPct.toFixed(0)}%). Board-riders are bitten almost as often (${board.total}) but die far less (${board.fatalPct.toFixed(0)}%) — a board and wetsuit buy time.`,
      view: 'activities',
    });
  }

  // A third provoked.
  const prov = incidents.filter((i) => i.prov === true).length;
  const unprov = incidents.filter((i) => i.prov === false).length;
  if (prov + unprov > 0) {
    out.push({
      severity: 'info',
      title: 'A third of bites are provoked',
      body: `${((prov / (prov + unprov)) * 100).toFixed(0)}% of incidents happened while the person was fishing, spearfishing, feeding or handling a shark. The classic unprovoked bite is the minority of the record.`,
      view: 'activities',
    });
  }

  // Summer peak.
  const peakM = months.indexOf(Math.max(...months));
  const summer = (months[11] + months[0] + months[1]);
  const winter = (months[5] + months[6] + months[7]);
  if (winter > 0) {
    const MN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    out.push({
      severity: 'info',
      title: `Summer is peak season — ${MN[peakM]} most of all`,
      body: `Summer (Dec–Feb) records ${summer} incidents against just ${winter} in winter (Jun–Aug), a ${(summer / winter).toFixed(1)}× difference. Warm water draws both sharks and swimmers inshore.`,
      view: 'seasonality',
    });
  }

  // NSW leads.
  if (states.length) {
    const top = states[0];
    out.push({
      severity: 'info',
      title: `${top.st} records the most incidents`,
      body: `${top.st} leads with ${top.total} incidents. Adjusted for population, however, sparsely settled coasts like the NT and WA carry a much higher rate per resident — raw counts mostly track where people live and swim.`,
      view: 'map',
    });
  }

  // Demographics.
  const male = incidents.filter((i) => i.sex === 'male').length;
  const female = incidents.filter((i) => i.sex === 'female').length;
  const ages = incidents.map((i) => i.age).filter((a): a is number => a != null);
  const medAge = median(ages);
  if (male + female > 0) {
    out.push({
      severity: 'info',
      title: 'Overwhelmingly young men',
      body: `${((male / (male + female)) * 100).toFixed(0)}% of victims are male, with a median age of ${medAge}. It reflects who spends the most time surfing, diving and spearfishing in open water.`,
      view: 'explorer',
    });
  }

  return out;
}
